import React from "react";

/*
 * LOGO AUPRÉA — reproduction fidèle du logo officiel (SVG inline).
 *
 * Le logo officiel utilise Playfair Display Bold (typo serif épaisse à forts pleins/déliés)
 * et l'or ambré #C8902C (plus riche que le laiton clair utilisé ailleurs sur le site).
 * On rend le logo comme SVG inline pour garantir un accès aux webfonts du document
 * (Playfair Display chargée dans index.css).
 *
 * Deux variantes fournies :
 *   • <Logo />       — pour fond clair (crème/blanc) : AU navy, PRÉA or, fond transparent
 *   • <LogoLight />  — pour fond foncé (navy/teal)   : AU blanc, PRÉA or, fond transparent
 *
 * Une variante complète <LogoFull /> (avec bloc navy officiel + citation) pour les pages
 * de marque (hero, invitation, export).
 */

const GOLD = "#C8902C";   // ambre officiel du logo
const NAVY = "#1B2A4A";   // navy officiel du logo
const WHITE = "#FFFFFF";
const CREAM = "#F3EFE6";

// Dimensions officielles (issues du fichier /public/brand/auprea-logo-principal.svg) :
// wordmark rendered avec font-size 220 → hauteur cap ~ 160 · width ~ 780 (AU) + 380 (PRÉA)
// Le SVG est calé pour un viewBox 1200×260 (wordmark seul) ou 1200×420 (avec citation).
const VBOX_WORDMARK = "0 0 1200 260";
const VBOX_FULL = "0 0 1200 420";

function Wordmark({ leftColor, rightColor = GOLD }) {
  return (
    <>
      <text
        x="50"
        y="215"
        fontFamily="'Playfair Display', Georgia, serif"
        fontWeight="700"
        fontSize="240"
        fill={leftColor}
        letterSpacing="-2"
      >
        AU
      </text>
      <text
        x="372"
        y="215"
        fontFamily="'Playfair Display', Georgia, serif"
        fontWeight="700"
        fontSize="240"
        fill={rightColor}
        letterSpacing="-2"
      >
        PRÉA
      </text>
    </>
  );
}

/* Variante fond clair — AU navy · PRÉA or ambré · fond transparent */
export function Logo({ size = 32, withSlogan = false, "data-testid": testid = "logo" }) {
  const w = size * 4.6;           // ratio wordmark seul ≈ 4.6
  const h = size;
  const wFull = size * 2.85;      // ratio avec citation ≈ 2.85
  const hFull = size;

  if (withSlogan) {
    return (
      <div className="inline-flex" data-testid={testid} style={{ lineHeight: 0 }}>
        <svg
          viewBox={VBOX_FULL}
          width={wFull}
          height={hFull}
          role="img"
          aria-label="AUPRÉA — Anticiper est un acte d'amour"
          style={{ display: "block" }}
        >
          <Wordmark leftColor={NAVY} rightColor={GOLD} />
          <text
            x="52"
            y="345"
            fontFamily="'Playfair Display', Georgia, serif"
            fontWeight="500"
            fontStyle="italic"
            fontSize="42"
            fill={NAVY}
            opacity="0.85"
          >
            « Anticiper est un acte d’amour. »
          </text>
        </svg>
      </div>
    );
  }
  return (
    <div className="inline-flex" data-testid={testid} style={{ lineHeight: 0 }}>
      <svg
        viewBox={VBOX_WORDMARK}
        width={w}
        height={h}
        role="img"
        aria-label="AUPRÉA"
        style={{ display: "block" }}
      >
        <Wordmark leftColor={NAVY} rightColor={GOLD} />
      </svg>
    </div>
  );
}

/* Variante fond foncé — AU blanc · PRÉA or · fond transparent (le fond navy est fourni par le contexte) */
export function LogoLight({ size = 32, withSlogan = false, "data-testid": testid = "logo-light" }) {
  const w = size * 4.6;
  const h = size;
  const wFull = size * 2.85;
  const hFull = size;
  if (withSlogan) {
    return (
      <div className="inline-flex" data-testid={testid} style={{ lineHeight: 0 }}>
        <svg
          viewBox={VBOX_FULL}
          width={wFull}
          height={hFull}
          role="img"
          aria-label="AUPRÉA — Anticiper est un acte d'amour"
          style={{ display: "block" }}
        >
          <Wordmark leftColor={WHITE} rightColor={GOLD} />
          <text
            x="52"
            y="345"
            fontFamily="'Playfair Display', Georgia, serif"
            fontWeight="500"
            fontStyle="italic"
            fontSize="42"
            fill={CREAM}
            opacity="0.92"
          >
            « Anticiper est un acte d’amour. »
          </text>
        </svg>
      </div>
    );
  }
  return (
    <div className="inline-flex" data-testid={testid} style={{ lineHeight: 0 }}>
      <svg
        viewBox={VBOX_WORDMARK}
        width={w}
        height={h}
        role="img"
        aria-label="AUPRÉA"
        style={{ display: "block" }}
      >
        <Wordmark leftColor={WHITE} rightColor={GOLD} />
      </svg>
    </div>
  );
}

/* Variante complète — bloc navy officiel avec citation (hero pages, invitations, exports) */
export function LogoFull({ size = 80, "data-testid": testid = "logo-full" }) {
  return (
    <div
      className="inline-flex"
      data-testid={testid}
      style={{
        background: NAVY,
        padding: `${size * 0.3}px ${size * 0.4}px`,
        borderRadius: 4,
        lineHeight: 0,
      }}
    >
      <LogoLight size={size} withSlogan />
    </div>
  );
}
